import { clsx } from 'clsx';
import { useState } from 'react';
import { Pressable, View } from 'react-native';
import colors from 'shared/library/colors';
import {
  PetCostBreakdown,
  PetCostCategory,
  PetCostStage,
  PetCostStageSummary,
  petCostColors,
  sumCosts,
} from 'shared/library/pet-costs';
import {
  Svg,
  Circle,
  G,
  Line,
  Path,
  Rect,
  SvgText,
} from 'shared/screens/breeds/breed-price-chart-svg';

const TOP = 12;
const BOTTOM = 30;
const LEFT = 52;
const RIGHT = 12;

type Point = { x: number; y: number };

// Monotone cubic interpolation (Fritsch–Carlson) so the curves never overshoot the data.
function curveThrough(points: Point[]) {
  const slopes = points.slice(1).map((point, index) => {
    const previous = points[index]!;

    return (point.y - previous.y) / (point.x - previous.x);
  });
  const tangents = points.map((_, index) => {
    if (index === 0) return slopes[0]!;
    if (index === points.length - 1) return slopes[slopes.length - 1]!;

    const before = slopes[index - 1]!;
    const after = slopes[index]!;

    return before * after <= 0 ? 0 : (2 * before * after) / (before + after);
  });

  return points
    .slice(1)
    .map((point, index) => {
      const previous = points[index]!;
      const third = (point.x - previous.x) / 3;

      return `C ${previous.x + third} ${previous.y + tangents[index]! * third} ${point.x - third} ${point.y - tangents[index + 1]! * third} ${point.x} ${point.y}`;
    })
    .join(' ');
}

function niceStep(maximum: number) {
  const rough = maximum / 5;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / magnitude;

  return (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude;
}

export function PetCostChart({
  years,
  categories,
  stages,
  selectedStage,
  accessibilityLabel,
  formatAxis,
  yearLabel,
  stageLabel,
  onSelectStage,
  className,
}: {
  years: PetCostBreakdown[];
  categories: PetCostCategory[];
  stages: PetCostStageSummary[];
  selectedStage: PetCostStageSummary;
  accessibilityLabel: string;
  formatAxis: (value: number) => string;
  yearLabel: (year: number) => string;
  stageLabel: (stage: PetCostStage) => string;
  onSelectStage: (stage: PetCostStage) => void;
  className?: string;
}) {
  // The box height comes from classes (fixed per breakpoint, or flex-grown), so the server render reserves the right space.
  const [{ width, height }, setSize] = useState({ width: 0, height: 0 });
  const plotWidth = width - LEFT - RIGHT;
  const plotHeight = height - TOP - BOTTOM;
  const baseline = TOP + plotHeight;
  const maximumTotal = Math.max(...years.map(sumCosts));
  const step = niceStep(maximumTotal);
  const maximum = Math.ceil(maximumTotal / step) * step;
  const gridValues = Array.from({ length: maximum / step + 1 }, (_, index) => index * step);
  // Every year gets an equal column; values sit in the column's centre.
  const xForYear = (year: number) => LEFT + ((year - 0.5) / years.length) * plotWidth;
  const yForValue = (value: number) => baseline - (value / maximum) * plotHeight;
  const labelEvery = plotWidth / years.length < 34 ? 2 : 1;
  const toEdges = (values: number[]): Point[] => [
    { x: LEFT, y: values[0]! },
    ...values.map((y, index) => ({ x: xForYear(index + 1), y })),
    { x: LEFT + plotWidth, y: values[values.length - 1]! },
  ];

  // Stack from the bottom up in the given order, keeping cumulative totals per year.
  const cumulative = years.map(() => 0);
  const layers = categories.flatMap((category) => {
    if (!years.some((year) => (year[category] ?? 0) > 0)) return [];

    const lower = toEdges(years.map((_, index) => yForValue(cumulative[index]!)));

    years.forEach((year, index) => {
      cumulative[index] = cumulative[index]! + (year[category] ?? 0);
    });

    const upper = toEdges(years.map((_, index) => yForValue(cumulative[index]!)));
    const reversedLower = [...lower].reverse();

    return [
      {
        category,
        d: `M ${upper[0]!.x} ${upper[0]!.y} ${curveThrough(upper)} L ${reversedLower[0]!.x} ${reversedLower[0]!.y} ${curveThrough(reversedLower)} Z`,
      },
    ];
  });
  const totalLine = cumulative.map((value, index) => ({
    x: xForYear(index + 1),
    y: yForValue(value),
  }));
  const totalPath = toEdges(totalLine.map((point) => point.y));
  const bandStart = (stage: PetCostStageSummary) =>
    LEFT + ((stage.firstYear - 1) / years.length) * plotWidth;
  const bandEnd = (stage: PetCostStageSummary) =>
    LEFT + (stage.lastYear / years.length) * plotWidth;

  return (
    <View
      className={clsx('h-56 md:h-72', className)}
      onLayout={(event) =>
        setSize({
          width: Math.round(event.nativeEvent.layout.width),
          height: Math.round(event.nativeEvent.layout.height),
        })
      }
    >
      {plotWidth > 0 && plotHeight > 0 && (
        <Svg
          width={width}
          height={height}
          // Absolute, so a flex-grown box never takes its height from the drawing.
          style={{ position: 'absolute', top: 0, left: 0 }}
          viewBox={`0 0 ${width} ${height}`}
          accessibilityRole={'image'}
          accessibilityLabel={accessibilityLabel}
        >
          <Rect
            x={bandStart(selectedStage)}
            y={TOP}
            width={bandEnd(selectedStage) - bandStart(selectedStage)}
            height={plotHeight}
            fill={colors.brand['green-light']}
          />

          {gridValues.map((value) => (
            <G key={value}>
              <Line
                x1={LEFT}
                x2={LEFT + plotWidth}
                y1={yForValue(value)}
                y2={yForValue(value)}
                stroke={colors.brand['brown-dark']}
                strokeOpacity={0.1}
                strokeWidth={1}
              />
              <SvgText
                x={LEFT - 8}
                y={yForValue(value) + 4}
                fill={colors.brand['brown-dark']}
                fillOpacity={0.6}
                fontSize={11}
                textAnchor={'end'}
              >
                {formatAxis(value)}
              </SvgText>
            </G>
          ))}

          {layers.map((layer) => (
            <Path
              key={layer.category}
              d={layer.d}
              fill={petCostColors[layer.category]}
              stroke={'white'}
              strokeWidth={1}
              strokeLinejoin={'round'}
            />
          ))}

          <Path
            d={`M ${totalPath[0]!.x} ${totalPath[0]!.y} ${curveThrough(totalPath)}`}
            fill={'none'}
            stroke={colors.brand['brown-dark']}
            strokeOpacity={0.55}
            strokeWidth={1.5}
          />

          {stages
            .filter((stage) => stage.stage !== selectedStage.stage)
            .map((stage) => (
              <Rect
                key={stage.stage}
                x={bandStart(stage)}
                y={TOP}
                width={bandEnd(stage) - bandStart(stage)}
                height={plotHeight}
                fill={colors.brand['cream-light']}
                fillOpacity={0.55}
              />
            ))}

          {stages.slice(1).map((stage) => (
            <Line
              key={stage.stage}
              x1={bandStart(stage)}
              x2={bandStart(stage)}
              y1={TOP}
              y2={baseline}
              stroke={colors.brand['brown-dark']}
              strokeOpacity={0.3}
              strokeDasharray={'3 4'}
              strokeWidth={1}
            />
          ))}

          {totalLine.map((point, index) =>
            index + 1 >= selectedStage.firstYear && index + 1 <= selectedStage.lastYear ? (
              <Circle
                key={index}
                cx={point.x}
                cy={point.y}
                r={4}
                fill={'white'}
                stroke={colors.brand.green}
                strokeWidth={2.5}
              />
            ) : null,
          )}

          <Line
            x1={LEFT}
            x2={LEFT + plotWidth}
            y1={baseline}
            y2={baseline}
            stroke={colors.brand['brown-dark']}
            strokeOpacity={0.3}
            strokeWidth={1}
          />

          {years.map((_, index) => {
            const year = index + 1;
            const selected = year >= selectedStage.firstYear && year <= selectedStage.lastYear;

            if (year !== 1 && year !== years.length && year % labelEvery !== 0) return null;

            return (
              <SvgText
                key={year}
                x={xForYear(year)}
                y={baseline + 19}
                fill={colors.brand['brown-dark']}
                fillOpacity={selected ? 1 : 0.55}
                fontSize={11}
                fontWeight={selected ? '700' : '400'}
                textAnchor={'middle'}
              >
                {year === 1 ? yearLabel(year) : String(year)}
              </SvgText>
            );
          })}
        </Svg>
      )}

      {/* Each life stage's column is a hit area, so tapping the chart picks the same period as the tabs. */}
      {plotWidth > 0 &&
        plotHeight > 0 &&
        stages.map((stage) => {
          const selected = stage.stage === selectedStage.stage;

          return (
            <Pressable
              key={stage.stage}
              accessibilityRole={'button'}
              accessibilityLabel={stageLabel(stage.stage)}
              accessibilityState={{ selected }}
              onPress={() => onSelectStage(stage.stage)}
              testID={`pet-costs-chart-stage-${stage.stage}`}
              className={clsx(
                'absolute rounded-sm transition-colors',
                !selected && 'hover:bg-brand-brown-dark/5',
              )}
              style={{
                left: bandStart(stage),
                width: bandEnd(stage) - bandStart(stage),
                top: TOP,
                height: plotHeight + BOTTOM,
              }}
            />
          );
        })}
    </View>
  );
}
